import { Outlet } from "@tanstack/react-router"
import { cn } from "design-system/utils"
import { tryCatchFallback } from "lopermut"
import { useAuthEmployeeQuery } from "modules/auth/hooks/useAuthEmployeeQuery"
import { useHasCompanyFeature } from "modules/auth/hooks/useHasCompanyFeature"
import { useNewSupportFlow } from "modules/auth/hooks/useNewSupportFlow"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { TimeRangeSelector } from "modules/shared/components/TimeRange/TimeRangeSelector"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  DashboardSyncContext,
  useIsDashboardSyncing,
} from "modules/dashboard/hooks/useDashboardQuery"
import { Route } from "routes/_base/_auth/insights"
import {
  ClusterDomainEnum,
  CompanyUsageKind,
  GetCanonicalTopicGroupedSchema,
  getTimeRange,
  resolveTimeDimension,
  TimeDimensionEnum,
} from "schema"
import { z } from "zod"
import { Activity } from "./Activity"
import { Signals } from "./Signals"
import { SyncIndicator } from "./SyncIndicator"
import { DomainSelector } from "./Treemaps/DomainSelector"
import { GroupBySelector } from "./Treemaps/GroupBySelector"
import { GroupedTreemap } from "./Treemaps/GroupedTreemap"
import { TreemapsFilters } from "./Treemaps/TreemapsFilters"

const DEFAULT_TIME_DIMENSION = TimeDimensionEnum.Day

const TREEMAP_COPY = {
  [ClusterDomainEnum.VoiceOfCustomer]: {
    title: "dashboard.voiceOfCustomer.title",
    description: "dashboard.voiceOfCustomer.description",
  },
  [ClusterDomainEnum.Support]: {
    title: "dashboard.support.title",
    description: "dashboard.support.description",
  },
  [ClusterDomainEnum.Question]: {
    title: "dashboard.question.title",
    description: "dashboard.question.description",
  },
  [ClusterDomainEnum.ActionRequest]: {
    title: "dashboard.actionRequest.title",
    description: "dashboard.actionRequest.description",
  },
  [ClusterDomainEnum.Issue]: {
    title: "dashboard.issue.title",
    description: "dashboard.issue.description",
  },
} as const

type DashboardTreemapDomain = keyof typeof TREEMAP_COPY

type Payload = Omit<z.infer<typeof GetCanonicalTopicGroupedSchema>, "domain" | "timeFrame"> & {
  domain?: DashboardTreemapDomain
}

const DashboardTreemapDomainSchema = z.enum([
  ClusterDomainEnum.VoiceOfCustomer,
  ClusterDomainEnum.Support,
  ClusterDomainEnum.Question,
  ClusterDomainEnum.ActionRequest,
  ClusterDomainEnum.Issue,
])

const DashboardFiltersSchema = GetCanonicalTopicGroupedSchema.omit({
  domain: true,
  timeFrame: true,
}).extend({
  domain: DashboardTreemapDomainSchema.optional(),
})

function getFiltersKey({ companyId }: { companyId: string }) {
  return `insights-filters-${companyId}`
}

const DEFAULT_PAYLOAD: Payload = {
  groupedBy: { type: "company" as const },
  filterBy: undefined,
}

function getInitialFilters({ companyId }: { companyId: string }) {
  const stored = localStorage.getItem(getFiltersKey({ companyId }))
  if (!stored) return DEFAULT_PAYLOAD

  const parsed = tryCatchFallback(() => JSON.parse(stored), undefined)
  const result = DashboardFiltersSchema.safeParse(parsed)

  return result.success ? result.data : DEFAULT_PAYLOAD
}

function onFiltersChange({ filters, companyId }: { filters: Payload; companyId: string }) {
  localStorage.setItem(getFiltersKey({ companyId }), JSON.stringify(filters))
}

export const Dashboard = () => {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const { t } = useTranslation()
  const { data: employee } = useAuthEmployeeQuery()
  const companyId = employee?.companyId ?? ""

  const hasFeedback = useHasCompanyFeature(CompanyUsageKind.Feedback)
  const isAmphoraCompany = useIsAmphoraCompany()
  const { developerMode } = useDeveloperMode()
  const { isEnabled: isNewSupportFlowEnabled } = useNewSupportFlow()
  const isSyncing = useIsDashboardSyncing()

  const [filters, setFilters] = React.useState<Payload>(() => getInitialFilters({ companyId }))

  const { timeFrame } = search
  const timeDimension = resolveTimeDimension(
    getTimeRange(timeFrame),
    search.timeDimension ?? DEFAULT_TIME_DIMENSION,
  )

  const availableDomains = useMemo(() => {
    const domains: DashboardTreemapDomain[] = []

    if (!isNewSupportFlowEnabled) domains.push(ClusterDomainEnum.Support)

    if (isNewSupportFlowEnabled || developerMode) {
      domains.push(
        ClusterDomainEnum.Issue,
        ClusterDomainEnum.Question,
        ClusterDomainEnum.ActionRequest,
      )
    }

    if (hasFeedback) domains.push(ClusterDomainEnum.VoiceOfCustomer)

    return domains
  }, [hasFeedback, developerMode, isNewSupportFlowEnabled])

  const selectedDomain =
    availableDomains.find((domain) => domain === filters.domain) ??
    availableDomains[0] ??
    ClusterDomainEnum.Support

  const treemapCopy = TREEMAP_COPY[selectedDomain]

  const activityFilters = useMemo(
    () => ({
      campaignId: filters.filterBy?.campaignId,
      segmentIds: filters.filterBy?.segments?.map((segment) => segment.segmentId),
      channel: filters.filterBy?.channel,
      conversationFlow: filters.filterBy?.conversationFlow,
    }),
    [filters.filterBy],
  )

  function onChange(partialFilters: Partial<Payload>) {
    setFilters((current) => {
      const newValue = { ...current, ...partialFilters }
      onFiltersChange({ filters: newValue, companyId })
      return newValue
    })
  }

  const syncingClassName = cn(
    "transition-opacity duration-300",
    isSyncing && "opacity-60 delay-200",
  )

  return (
    <DashboardSyncContext.Provider value={true}>
      <div className="flex flex-col gap-4 flex-1 min-h-[calc(100dvh-6.75rem)] md:min-h-[calc(100dvh-8.25rem)]">
        <SyncIndicator isSyncing={isSyncing} />
        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
          <TreemapsFilters
            filterBy={filters.filterBy}
            onChange={(filterBy) => onChange({ filterBy })}
          />
          <TimeRangeSelector
            value={timeFrame}
            dimension={timeDimension}
            onChange={(value) => navigate({ search: (current) => ({ ...current, ...value }) })}
          />
        </div>
        <div className={cn("shrink-0", syncingClassName)}>
          <Activity
            timeFrame={timeFrame}
            timeDimension={timeDimension}
            filters={activityFilters}
            isNewSupportFlowEnabled={isNewSupportFlowEnabled}
          />
        </div>
        {search.signals && (
          <div className={cn("shrink-0", syncingClassName)}>
            <Signals />
          </div>
        )}
        <div className="flex flex-col gap-4 flex-1 min-h-0">
          <div className="flex flex-col md:flex-row gap-2 md:items-end items-start w-full shrink-0">
            <DomainSelector
              value={selectedDomain}
              options={availableDomains}
              onChange={(domain) => onChange({ domain })}
            />
            <div className="flex flex-row flex-wrap gap-2 items-end md:ml-auto">
              <GroupBySelector
                value={filters.groupedBy}
                onChange={(groupedBy) => onChange({ groupedBy })}
              />
            </div>
          </div>
          {treemapCopy && (
            <div className={cn("flex flex-col flex-1 min-h-0", syncingClassName)}>
              <GroupedTreemap
                key={selectedDomain}
                {...filters}
                timeFrame={timeFrame}
                domain={selectedDomain}
                groupNonCategoryTopics={isAmphoraCompany}
                setFilters={setFilters}
                onFiltersChange={onChange}
                title={t(treemapCopy.title)}
                description={t(treemapCopy.description)}
              />
            </div>
          )}
        </div>
      </div>
      <Outlet />
    </DashboardSyncContext.Provider>
  )
}
